import React, { useState, useEffect } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Plus, Search, Loader2, ExternalLink, Calendar, CheckCircle, FileSpreadsheet } from 'lucide-react';
import { Navigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { pmt2Service } from '../../services/pmt2Service';
import { useAuth } from '@/hooks/useAuth';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { amazonIndexCheckerService } from '../../services/amazonIndexCheckerService';
import { IndexCheckResult, IndexCheckSession, ASINScheduleStatus, ImportResult, SyncResult, SheetsConfig } from '../../types/amazonIndexChecker';
import PreviousResults from '../../components/amazon-index-checker/PreviousResults';
import CheckingStatusCard from '../../components/amazon-index-checker/CheckingStatusCard';
import IndexingMetricsCard from '../../components/amazon-index-checker/IndexingMetricsCard';
import GoogleSheetsImportModal from '../../components/amazon-index-checker/GoogleSheetsImportModal';
import ImageModal from '../../components/amazon-index-checker/ImageModal';
import DashboardOverview from '../../components/amazon-index-checker/DashboardOverview';
import CompanySelectedView from '../../components/amazon-index-checker/CompanySelectedView';
import { doc, onSnapshot, collection, DocumentChange, QuerySnapshot, DocumentData, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { PMT2Product } from '@/types/pmt2Types';

const AmazonIndexCheckerPage: React.FC = () => {
  usePageTitle('Amazon Index Checker');
  
  const { user } = useAuth();
  const {
    selectedCompany,
    selectedProduct,
    products,
    setSelectedCompany,
    setSelectedProduct,
    loading: selectionLoading,
    error: selectionError
  } = usePMT3Selection();
  
  const { onPasteTermsClick, onSheetsTermsClick, sheetsConfig, onDashboardStateChange } = useOutletContext<{
    onPasteTermsClick?: React.Dispatch<React.SetStateAction<(() => void) | null>>;
    onSheetsTermsClick?: React.Dispatch<React.SetStateAction<(() => void) | null>>;
    sheetsConfig?: SheetsConfig | null;
    onDashboardStateChange?: (isDashboard: boolean) => void;
  }>();
  
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [showAddTerms, setShowAddTerms] = useState(false);
  const [terms, setTerms] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [checkingProgress, setCheckingProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState<IndexCheckResult[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [allResults, setAllResults] = useState<IndexCheckResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [rerunningTerm, setRerunningTerm] = useState<string | null>(null);
  const [showStatusCard, setShowStatusCard] = useState(false);
  const [isCheckingComplete, setIsCheckingComplete] = useState(false);
  const [currentSession, setCurrentSession] = useState<IndexCheckSession | null>(null);
  const [sessionUnsubscribe, setSessionUnsubscribe] = useState<(() => void) | null>(null);
  const [asinScheduleStatus, setAsinScheduleStatus] = useState<ASINScheduleStatus>({ isScheduled: false });
  const [isScheduleToggling, setIsScheduleToggling] = useState(false);
  const [liveMetrics, setLiveMetrics] = useState({ checked: 0, indexed: 0, notIndexed: 0 });
  const [failedTermsCount, setFailedTermsCount] = useState(0);

  // Memory leak prevention: Track active listeners
  const [activeListenerCount, setActiveListenerCount] = useState(0);

  // Dashboard view state
  const [isDashboardView, setIsDashboardView] = useState(true);

  // Sheets modal state
  const [showSheetsModal, setShowSheetsModal] = useState(false);
  const [sheetsImportResult, setSheetsImportResult] = useState<ImportResult | SyncResult | null>(null);
  
  // Image modal state
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Determine view based on URL params and explicit navigation only
  useEffect(() => {
    const view = searchParams.get('view');
    const company = searchParams.get('company');
    const asin = searchParams.get('asin');
    
    // Only switch to product view if explicitly set via URL params
    if (company && asin && view === 'product') {
      console.log('🔄 Switching to product view from URL params:', { company, asin });
      setIsDashboardView(false);
    } else if (view === 'company' && company) {
      // Company selected but no ASIN - show company placeholder
      console.log('🏢 Switching to company view from URL params:', { company });
      setIsDashboardView(false);
    } else if (view === 'dashboard' || (!view && !company && !asin)) {
      // Explicit dashboard view or no params = dashboard
      console.log('🏠 Staying in dashboard view');
      setIsDashboardView(true);
    }
    // Remove the selectedCompany && selectedProduct check to prevent auto-switching
    
  }, [searchParams]); // Remove isDashboardView and other deps to prevent loop
  
  // Separate effect to notify layout about dashboard state changes
  useEffect(() => {
    if (onDashboardStateChange) {
      onDashboardStateChange(isDashboardView);
    }
  }, [isDashboardView, onDashboardStateChange]);

  // Handle ASIN card clicks from dashboard
  const handleASINCardClick = async (company: string, asin: string) => {
    console.log(`🔄 Navigating to product view: ${company}/${asin}`);
    
    // Update URL params
    setSearchParams({
      view: 'product',
      company,
      asin
    });
    
    try {
      // First, set the company (this will trigger product loading and clear current product)
      setSelectedCompany(company);
      
      // If the company is different from current, we need to wait for products to load
      if (selectedCompany !== company) {
        // Get products for the new company
        const productsData = await pmt2Service.getProductsByCompany(company);
        
        // Find the product by ASIN
        const foundProduct = productsData.find((p: PMT2Product) => p.id === asin);
        
        if (foundProduct) {
          // Set the product after products are loaded
          setTimeout(() => {
            setSelectedProduct(foundProduct);
          }, 100); // Small delay to ensure context has updated
        } else {
          console.warn(`Product with ASIN ${asin} not found in company ${company}`);
        }
      } else {
        // Same company, just find the product from current products list
        const foundProduct = products.find((p: PMT2Product) => p.id === asin);
        if (foundProduct) {
          setSelectedProduct(foundProduct);
        } else {
          console.warn(`Product with ASIN ${asin} not found in current products list`);
        }
      }
    } catch (error) {
      console.error('Error setting product:', error);
    }
    
    // Switch to product view
    setIsDashboardView(false);
    
    // Notify layout
    if (onDashboardStateChange) {
      onDashboardStateChange(false);
    }
  };

  // Handle switching back to dashboard
  const handleBackToDashboard = () => {
    console.log('🏠 Switching back to dashboard view');
    setSearchParams({});
    setIsDashboardView(true);
    
    // Clear selections to show dashboard
    setSelectedCompany('');
    setSelectedProduct(null);
    
    // Notify layout
    if (onDashboardStateChange) {
      onDashboardStateChange(true);
    }
  };
  
  
  // Load last updated time, historical results, and check for active sessions
  useEffect(() => {
    if (selectedCompany && selectedProduct) {
      loadHistoricalData();
      loadScheduleStatus();
      checkForActiveSession(); // 🚀 NEW: Check for ongoing sessions on page load
    }
  }, [selectedCompany, selectedProduct]);

  // Update live metrics when results change
  useEffect(() => {
    const checked = allResults.length;
    const indexed = allResults.filter(r => r.isIndexed).length;
    const notIndexed = allResults.filter(r => !r.isIndexed).length;
    
    setLiveMetrics({ checked, indexed, notIndexed });
  }, [allResults]);

  // Set up the callbacks for the layout's buttons
  useEffect(() => {
    if (onPasteTermsClick && selectedProduct) {
      onPasteTermsClick(handlePasteTermsClick);
    } else if (onPasteTermsClick) {
      onPasteTermsClick(null);
    }

    if (onSheetsTermsClick) {
      onSheetsTermsClick(() => setShowSheetsModal(true));
    }
  }, [selectedProduct, onPasteTermsClick, onSheetsTermsClick]);

  // 🚀 MEMORY LEAK FIX: Comprehensive cleanup effect
  useEffect(() => {
    return () => {
      // Clean up session listener on unmount
      console.log('🧹 Cleaning up listeners on unmount', { activeListenerCount });
      if (sessionUnsubscribe) {
        sessionUnsubscribe();
        setActiveListenerCount(0);
      }
    };
  }, [sessionUnsubscribe, activeListenerCount]);

  // 🚀 MEMORY LEAK FIX: Cleanup on navigation
  useEffect(() => {
    return () => {
      if (isDashboardView && sessionUnsubscribe) {
        console.log('🧹 Cleaning up listeners on dashboard navigation');
        sessionUnsubscribe();
        setSessionUnsubscribe(null);
        setActiveListenerCount(0);
      }
    };
  }, [isDashboardView, sessionUnsubscribe]);
  
  // Redirect if not authenticated
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  const loadHistoricalData = async () => {
    if (!selectedCompany || !selectedProduct) return;
    
    try {
      // Load all term results directly from individual documents
      const allTermResults = await amazonIndexCheckerService.getAllTermResults(
        selectedCompany,
        selectedProduct.id
      );
      
      setAllResults(allTermResults);
      
      // Set last updated time based on the most recent result
      if (allTermResults.length > 0) {
        const mostRecent = allTermResults[0]; // Already sorted by timestamp
        setLastUpdated(new Date(mostRecent.timestamp));
      } else {
        setLastUpdated(null);
      }
    } catch (err) {
      console.error('Error loading historical data:', err);
    }
  };

  const loadScheduleStatus = async () => {
    if (!selectedCompany || !selectedProduct) return;
    
    try {
      const status = await amazonIndexCheckerService.getASINScheduleStatus(
        selectedCompany,
        selectedProduct.id
      );
      setAsinScheduleStatus(status);
    } catch (err) {
      console.error('Error loading schedule status:', err);
    }
  };

  // 🚀 NEW: Check for active processing sessions on page load
  const checkForActiveSession = async () => {
    if (!selectedCompany || !selectedProduct) return;

    try {
      // Query for active sessions
      const sessionsCollectionRef = collection(db, `amz_index_sessions/${selectedCompany}/${selectedProduct.id}`);
      const sessionsSnapshot = await getDocs(sessionsCollectionRef);
      
      let activeSession: any = null;
      sessionsSnapshot.forEach(doc => {
        const sessionData = doc.data();
        if (sessionData.status === 'processing') {
          if (sessionData.type === 'master' || !activeSession) {
            activeSession = { id: doc.id, ...sessionData };
          }
        }
      });
  
      if (activeSession) {
        await restoreActiveSession(activeSession);
      }
    } catch (err) {
      console.error('Error checking for active session:', err);
    }
  };

  // 🚀 MEMORY LEAK FIX: Optimized session restoration with proper cleanup
  const restoreActiveSession = async (sessionData: any) => {
    if (!selectedCompany || !selectedProduct) return;

    console.log('🔄 Restoring active session with memory optimization', { sessionId: sessionData.id });
    
    // Clean up any existing listeners first
    if (sessionUnsubscribe) {
      console.log('🧹 Cleaning up existing listeners before restoration');
      sessionUnsubscribe();
      setActiveListenerCount(0);
    }

    const sessionId = sessionData.id;
    const totalTerms = sessionData.totalTerms || 0;
    const processedTerms = sessionData.processedTerms || 0;

    // Restore UI state
    setIsChecking(processedTerms < totalTerms);
    setShowStatusCard(true);
    setCheckingProgress({ current: processedTerms, total: totalTerms });
    setCurrentSession(sessionData);
    setIsCheckingComplete(processedTerms >= totalTerms);

    // Set up real-time listeners with memory optimization
    const sessionPath = `amz_index_sessions/${selectedCompany}/${selectedProduct.id}/${sessionId}`;
    const sessionDocRef = doc(db, sessionPath);

    // 🚀 OPTIMIZED: Debounced progress updates to prevent excessive re-renders
    let progressUpdateTimeout: NodeJS.Timeout | null = null;
    const debouncedProgressUpdate = (processed: number, total: number) => {
      if (progressUpdateTimeout) clearTimeout(progressUpdateTimeout);
      progressUpdateTimeout = setTimeout(() => {
        setCheckingProgress({ current: processed, total: total });
      }, 100); // 100ms debounce
    };

    // Session progress listener with error handling
    const sessionUnsubscribeFunc = onSnapshot(sessionDocRef, (doc) => {
      if (doc.exists()) {
        const sessionData = doc.data();
        const processed = sessionData?.processedTerms || 0;
        const total = sessionData?.totalTerms || 0;
        const status = sessionData?.status || 'processing';

        // Debounced progress update
        debouncedProgressUpdate(processed, total);

        // Check if session completed
        if (status === 'completed') {
          setIsCheckingComplete(true);
          setIsChecking(false);
          setLastUpdated(new Date());
          loadHistoricalData();
        }
      }
    }, (error) => {
      console.error('Session listener error:', error);
      setError('Connection to processing session lost');
    });

    // 🚀 OPTIMIZED: Lightweight terms listener with change tracking
    const termsCollectionRef = collection(db, `amz_index/${selectedCompany}/${selectedProduct.id}`);
    let lastProcessedCount = processedTerms;

    const termsUnsubscribeFunc = onSnapshot(termsCollectionRef, (snapshot) => {
      // Only process if we have actual changes for current session
      const changes = snapshot.docChanges().filter(change => {
        const data = change.doc.data();
        return (data.sessionId === sessionId || data.masterSessionId === sessionId) &&
               change.type === 'added';
      });

      if (changes.length > 0) {
        const newResults: IndexCheckResult[] = changes.map(change => {
          const termData = change.doc.data();
          return {
            term: termData.term,
            asin: termData.asin,
            isIndexed: termData.isIndexed,
            position: termData.position,
            timestamp: termData.timestamp || new Date().toISOString(),
            searchUrl: termData.searchUrl,
            pageNumber: termData.pageNumber
          };
        });

        // Batch update results
        setResults(prev => [...newResults, ...prev]);
        newResults.forEach(result => updateResultsAndTimestamp(result));
        lastProcessedCount += changes.length;
      }
    }, (error) => {
      console.error('Terms listener error:', error);
    });

    // 🚀 MEMORY LEAK FIX: Enhanced cleanup function
    const combinedCleanup = () => {
      console.log('🧹 Cleaning up session listeners');
      if (progressUpdateTimeout) clearTimeout(progressUpdateTimeout);
      sessionUnsubscribeFunc();
      termsUnsubscribeFunc();
      setSessionUnsubscribe(null);
      setActiveListenerCount(0);
    };

    setSessionUnsubscribe(() => combinedCleanup);
    setActiveListenerCount(2); // Track active listeners
  };

  const handleRerunTerm = async (term: string) => {
    if (!selectedCompany || !selectedProduct || !user?.email) return;
    
    setRerunningTerm(term);
    try {
      const result = await amazonIndexCheckerService.recheckSingleTerm(
        term,
        selectedProduct.id,
        selectedCompany,
        user.email
      );
      
      // Update all results and last updated time
      updateResultsAndTimestamp(result);
    } catch (err) {
      console.error('Error rerunning term:', err);
      setError(`Failed to rerun term "${term}". Please try again.`);
    } finally {
      setRerunningTerm(null);
    }
  };

  const updateResultsAndTimestamp = (newResult: IndexCheckResult) => {
    // Update results - remove old entry for this term and add new one
    setAllResults(prev => {
      const filtered = prev.filter(r => r.term !== newResult.term);
      return [newResult, ...filtered].sort((a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
    });
    
    // Update last updated time
    setLastUpdated(new Date());
  };

  const handleToggleASINSchedule = async () => {
    if (!selectedCompany || !selectedProduct || !user?.email) return;
    
    setIsScheduleToggling(true);
    try {
      if (asinScheduleStatus.isScheduled) {
        // Disable scheduling
        await amazonIndexCheckerService.disableASINScheduling(selectedCompany, selectedProduct.id);
        setAsinScheduleStatus({ isScheduled: false });
      } else {
        // Enable scheduling
        const scheduledCheck = await amazonIndexCheckerService.enableASINScheduling(
          selectedCompany,
          selectedProduct.id,
          user.email
        );
        
        setAsinScheduleStatus({
          isScheduled: true,
          nextCheckDate: scheduledCheck.nextCheckDate,
          frequency: scheduledCheck.frequency === 'quarterly' ? 'quarterly' : 'monthly',
          scheduledCheckId: scheduledCheck.id,
          termsCount: scheduledCheck.terms.length,
          lastUpdated: scheduledCheck.lastChecked
        });
      }
    } catch (err) {
      console.error('Error toggling ASIN schedule:', err);
      setError(err instanceof Error ? err.message : 'Failed to toggle schedule. Please try again.');
    } finally {
      setIsScheduleToggling(false);
    }
  };

  const handleCloseStatusCard = () => {
    setShowStatusCard(false);
    setIsCheckingComplete(false);
    setCheckingProgress({ current: 0, total: 0 });
  };
  
  const handlePasteTermsClick = () => {
    setShowAddTerms(true);
    setTerms('');
    setResults([]);
    setError(null);
  };

  const handleSheetsTermsClick = () => {
    setShowSheetsModal(true);
  };
  
  const handleCheckIndexing = async () => {
    if (!terms.trim()) {
      setError('Please enter at least one term to check');
      return;
    }
    
    if (!selectedCompany || !selectedProduct) {
      setError('Please select a company and product first');
      return;
    }
    
    // Immediately close the Add Terms component and show Results table
    setShowAddTerms(false);
    setIsChecking(true);
    setError(null);
    setResults([]);
    
    const termsList = terms.split('\n').filter(t => t.trim());
    setCheckingProgress({ current: 0, total: termsList.length });
    
    // Show status card immediately
    setShowStatusCard(true);
    setIsCheckingComplete(false);
    
    try {
      // Generate session ID for batch processing
      const sessionId = `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // STEP 1: Start backend processing FIRST
      const session = await amazonIndexCheckerService.checkMultipleTerms(
        termsList,
        selectedProduct.id,
        selectedCompany,
        user.email || '',
        sessionId
      );
      
      setCurrentSession(session);
      
      // STEP 2: After backend confirms start, set up real-time listeners
      const sessionPath = `amz_index_sessions/${selectedCompany}/${selectedProduct.id}/${sessionId}`;
      const sessionDocRef = doc(db, sessionPath);
      
      // Wait longer for chunked processing to initialize
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // 🚀 MEMORY LEAK FIX: Clean up any existing listeners first
      if (sessionUnsubscribe) {
        console.log('🧹 Cleaning up existing listeners before new session');
        sessionUnsubscribe();
        setActiveListenerCount(0);
      }

      // 🚀 OPTIMIZED: Debounced progress updates to prevent excessive re-renders
      let progressUpdateTimeout: NodeJS.Timeout | null = null;
      const debouncedProgressUpdate = (processed: number, total: number) => {
        if (progressUpdateTimeout) clearTimeout(progressUpdateTimeout);
        progressUpdateTimeout = setTimeout(() => {
          setCheckingProgress({ current: processed, total: total });
        }, 100); // 100ms debounce
      };

      // Set up optimized real-time listener for session progress
      const sessionUnsubscribeFunc = onSnapshot(sessionDocRef, (doc) => {
        if (doc.exists()) {
          const sessionData = doc.data();
          
          const processed = sessionData?.processedTerms || 0;
          const total = sessionData?.totalTerms || 0;
          const status = sessionData?.status || 'processing';
  
          // Debounced progress update
          debouncedProgressUpdate(processed, total);
  
          // Check if session is complete
          if (status === 'completed') {
            setIsCheckingComplete(true);
            setIsChecking(false);
            setLastUpdated(new Date());
            loadHistoricalData();
          } else if (status === 'chunks-created') {
            // Keep showing progress for chunked processing
            setIsChecking(true);
            setIsCheckingComplete(false);
          } else if (status === 'processing' && processed > 0) {
            // Ensure UI reflects active processing
            setIsChecking(true);
            setIsCheckingComplete(false);
          }
        }
      }, (error) => {
        console.error('Session listener error:', error);
        setError('Progress tracking temporarily unavailable. Processing continues in background.');
      });
      
      // 🚀 MEMORY LEAK FIX: Optimized terms listener with change tracking
      const termsCollectionRef = collection(db, `amz_index/${selectedCompany}/${selectedProduct.id}`);
      let processedTermCount = 0;
      
      const termsUnsubscribeFunc = onSnapshot(termsCollectionRef, (snapshot) => {
        // 🚀 CRITICAL: Only process document changes, not the entire collection
        const changes = snapshot.docChanges().filter(change => {
          const data = change.doc.data();
          return (data.sessionId === sessionId || data.masterSessionId === sessionId) &&
                 change.type === 'added';
        });

        if (changes.length > 0) {
          console.log(`📊 Processing ${changes.length} new term results for session ${sessionId}`);
          
          const newResults: IndexCheckResult[] = changes.map(change => {
            const termData = change.doc.data();
            return {
              term: termData.term,
              asin: termData.asin,
              isIndexed: termData.isIndexed,
              position: termData.position,
              timestamp: termData.timestamp || new Date().toISOString(),
              searchUrl: termData.searchUrl,
              pageNumber: termData.pageNumber
            };
          });
          
          // 🚀 OPTIMIZED: Batch state updates
          setResults(prev => [...newResults, ...prev]);
          newResults.forEach(result => updateResultsAndTimestamp(result));
          processedTermCount += changes.length;
        }
      }, (error) => {
        console.error('Terms listener error:', error);
      });
      
      // 🚀 MEMORY LEAK FIX: Enhanced cleanup function
      const combinedCleanup = () => {
        console.log('🧹 Cleaning up batch processing listeners');
        if (progressUpdateTimeout) clearTimeout(progressUpdateTimeout);
        sessionUnsubscribeFunc();
        termsUnsubscribeFunc();
        setSessionUnsubscribe(null);
        setActiveListenerCount(0);
      };
      
      setSessionUnsubscribe(() => combinedCleanup);
      setActiveListenerCount(2); // Track active listeners
      
      // Update ASIN scheduling if it was enabled (add new terms to existing schedule)
      if (asinScheduleStatus.isScheduled) {
        try {
          await amazonIndexCheckerService.updateASINScheduling(
            selectedCompany,
            selectedProduct.id,
            user.email || ''
          );
          // Refresh schedule status to reflect updated term count
          await loadScheduleStatus();
        } catch (scheduleErr) {
          console.error('Error updating ASIN scheduling:', scheduleErr);
        }
      }
      
    } catch (err) {
      console.error('Error checking indexing:', err);
      setError('Failed to check indexing. Please try again.');
      // On error, hide the status card and reset state
      setShowStatusCard(false);
      setCheckingProgress({ current: 0, total: 0 });
      setIsChecking(false);
      setIsCheckingComplete(false);
    }
  };

  const handleSheetsImportSuccess = (result: ImportResult | SyncResult) => {
    setSheetsImportResult(result);
    
    // Don't auto-close modal for sync operations with 0 new terms
    // Let the modal handle its own closing behavior
    const isSync = 'wasSynced' in result && result.wasSynced;
    const hasNewTerms = result.newTerms > 0;
    
    // Handle sheets import success
    
    if (!(isSync && !hasNewTerms)) {
      // Close modal for imports or syncs with new terms
      setShowSheetsModal(false);
    }
    
    if (result.success) {
      // Refresh historical data to show new terms
      loadHistoricalData();
      // Update schedule status if needed
      loadScheduleStatus();
      // Show success notification or update UI
      setError(null); // Clear any errors
      
      // If terms were imported, show the "Check Indexing" form with the imported terms
      if (result.newTerms > 0 && result.terms && result.terms.length > 0) {
        setShowAddTerms(true); // Show the form
        setTerms(result.terms.join('\n')); // Pre-populate with imported terms
        // Pre-populate form with imported terms
      }
      
      // Don't auto-dismiss - let user close manually
    }
  };

  const handleDismissImportNotification = () => {
    setSheetsImportResult(null);
  };

  const handleSheetsModalClose = () => {
    setShowSheetsModal(false);
    // Don't clear results if we have new terms imported - let user dismiss manually
    if (sheetsImportResult && sheetsImportResult.newTerms === 0) {
      setSheetsImportResult(null);
    }
    // If newTerms > 0, keep the notification persistent for user to dismiss manually
  };
  
  // Get all image URLs from the product (copied from PMT2/ProductHeader)
  const getProductImages = (product: PMT2Product | null): string[] => {
    if (!product) return [];
    
    const images: string[] = [];
    
    // Check for numbered image URLs (Image URL 1, Image URL 2, etc.)
    for (let i = 1; i <= 8; i++) {
      const imageKey = `Image URL ${i}`;
      if (product[imageKey] && typeof product[imageKey] === 'string' &&
          product[imageKey] !== '--' && product[imageKey] !== '_' &&
          product[imageKey] !== '-' && !product[imageKey].startsWith('_')) {
        images.push(product[imageKey]);
      }
    }
    
    // If no numbered images found, check for the generic Image URL field
    if (images.length === 0 && product['Image URL'] &&
        typeof product['Image URL'] === 'string' &&
        product['Image URL'] !== '--' && product['Image URL'] !== '_' &&
        product['Image URL'] !== '-' && !product['Image URL'].startsWith('_')) {
      images.push(product['Image URL']);
    }
    
    return images;
  };

  const images = selectedProduct ? getProductImages(selectedProduct) : [];
  const productTitle = selectedProduct ? (selectedProduct['Product Name'] || selectedProduct.Title || selectedProduct.id) : '';
  
  // Handle navigation in the modal
  const handlePrevious = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex === 0 ? images.length - 1 : prevIndex - 1));
  };
  
  const handleNext = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
  };

  // Show loading or error states
  if (selectionError) {
    return (
      <div className="container mx-auto py-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{selectionError}</AlertDescription>
        </Alert>
      </div>
    );
  }
  
  if (selectionLoading) {
    return (
      <div className="container mx-auto py-6 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }
  
  // Show dashboard view by default or when no product is selected
  if (isDashboardView) {
    return <DashboardOverview onASINCardClick={handleASINCardClick} />;
  }

  // Show company selected view when company is chosen but no product
  const view = searchParams.get('view');
  const company = searchParams.get('company');
  const asin = searchParams.get('asin');
  
  if (view === 'company' && company && !asin && selectedCompany) {
    return (
      <CompanySelectedView
        companyName={selectedCompany}
        productsCount={products.length}
      />
    );
  }

  // Handle transition state: URL has product params but context hasn't loaded yet
  if (view === 'product' && company && asin && selectedCompany && !selectedProduct) {
    // Try to find the product from the current products list
    const urlProduct = products.find((p: PMT2Product) => p.id === asin);
    if (urlProduct) {
      // Set the product to prevent showing fallback
      setSelectedProduct(urlProduct);
    }
    // Show loading state while context catches up
    return (
      <div className="container mx-auto py-6 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-4 border-blue-200 dark:border-blue-900 rounded-full animate-spin border-t-blue-600 dark:border-t-blue-400 mx-auto"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading product data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Product Header - show only if product is selected */}
      {selectedProduct && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            {/* Left Section: Image and Title */}
            <div className="flex items-start gap-4 flex-grow">
              {/* Product Image (Clickable for Modal) - Only show if images exist */}
              {images.length > 0 && (
                <button
                  onClick={() => setIsImageModalOpen(true)}
                  className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:opacity-80 transition-opacity"
                  aria-label={`View ${productTitle} images`}
                >
                  <img
                    src={images[0]}
                    alt={productTitle}
                    className="w-full h-full object-cover"
                  />
                </button>
              )}

              {/* Title and ASIN/Sheets Link */}
              <div>
                <h2 className="text-xl font-bold leading-tight mb-1">
                  {productTitle}
                </h2>
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-sm">
                  <span className="font-medium text-gray-500 dark:text-gray-400">ASIN:</span>
                  <a
                    href={`https://www.amazon.com/dp/${selectedProduct.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 flex items-center gap-1 hover:underline"
                  >
                    {selectedProduct.id} <ExternalLink className="h-3 w-3" />
                  </a>
                  {/* Conditional Sheets Link */}
                  {sheetsConfig?.spreadsheetId && (
                    <>
                      <span className="text-gray-300 dark:text-gray-600">|</span>
                      <a
                        href={`https://docs.google.com/spreadsheets/d/${sheetsConfig.spreadsheetId}/edit#gid=0`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 dark:text-green-400 flex items-center gap-1 hover:underline"
                      >
                        Sheets <FileSpreadsheet className="h-3 w-3" />
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Right Section: Last Updated */}
            <div className="flex-shrink-0 text-right">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated</div>
              <div>
                {lastUpdated ? lastUpdated.toLocaleString() : 'Never checked'}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      {selectedProduct ? (
        <div>
          {/* Content Container - 30% less wide */}
          <div className="max-w-4xl mx-auto space-y-6">
          
          {/* Add Terms Section OR Metrics Card */}
          {!showAddTerms ? (
            // Show Add Terms card only if no historical results exist
            allResults.length === 0 && !isChecking ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center space-y-4">
                    <p className="text-gray-600 dark:text-gray-400">
                      Check if your product is indexed for specific search terms on Amazon
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2 justify-center">
                      <Button onClick={handlePasteTermsClick} size="lg" variant="default">
                        <Plus className="h-5 w-5 mr-2" />
                        Paste Terms
                      </Button>
                      <Button onClick={handleSheetsTermsClick} size="lg" variant="outline">
                        <FileSpreadsheet className="h-5 w-5 mr-2" />
                        Sheets Terms
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              // Show Metrics Card when results exist or checking
              <IndexingMetricsCard
                termsChecked={liveMetrics.checked}
                termsIndexed={liveMetrics.indexed}
                termsNotIndexed={liveMetrics.notIndexed}
              />
            )
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Check Indexing for Terms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Persistent Import Notification */}
                {sheetsImportResult && sheetsImportResult.success && sheetsImportResult.newTerms > 0 && (
                  <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <AlertDescription className="text-green-700 dark:text-green-300 pr-8">
                      <div className="font-medium mb-1">Google Sheets Import Complete!</div>
                      <div className="text-sm space-y-1">
                        <div>• {sheetsImportResult.newTerms} new terms imported</div>
                        <div>• {sheetsImportResult.duplicates} duplicates skipped</div>
                        <div>• {sheetsImportResult.totalTerms} total terms in sheet</div>
                        <div className="mt-2 text-xs text-green-600 dark:text-green-400">
                          Terms are ready for index checking. Review and click "Check Indexing" when ready.
                        </div>
                      </div>
                    </AlertDescription>
                    <button
                      onClick={handleDismissImportNotification}
                      className="absolute top-2 right-2 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
                      aria-label="Close notification"
                    >
                      ×
                    </button>
                  </Alert>
                )}
                
                <div>
                  <Label htmlFor="terms">
                    Enter terms to check (one per line)
                  </Label>
                  <p className="text-sm text-gray-500 mb-2">
                    Note: The ASIN ({selectedProduct.id}) will be automatically added to each search
                  </p>
                  <Textarea
                    id="terms"
                    placeholder="12in paint mixing stick&#10;acrylic paint supplies&#10;artist brushes"
                    value={terms}
                    onChange={(e) => setTerms(e.target.value)}
                    rows={8}
                    className="font-mono"
                    disabled={isChecking}
                  />
                </div>
                
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="flex gap-2">
                  <Button
                    onClick={handleCheckIndexing}
                    disabled={isChecking || !terms.trim()}
                    className="flex-1"
                  >
                    {isChecking ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Checking {checkingProgress.current}/{checkingProgress.total}...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-2" />
                        Check Indexing
                      </>
                    )}
                  </Button>
                  {!isChecking && (
                    <Button
                      variant="outline"
                      onClick={() => setShowAddTerms(false)}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Status Card - Above Results table */}
          {showStatusCard && (
            <div className="mb-6">
              <CheckingStatusCard
                isVisible={showStatusCard}
                isChecking={isChecking}
                isComplete={isCheckingComplete}
                currentCount={checkingProgress.current}
                totalCount={checkingProgress.total}
                onClose={handleCloseStatusCard}
              />
            </div>
          )}
          
          {/* Results Table - show during checking or when there are results */}
          {!showAddTerms && (allResults.length > 0 || isChecking) && (
            <PreviousResults
              results={allResults}
              company={selectedCompany}
              asin={selectedProduct.id}
              userEmail={user.email || ''}
              onRerunTerm={handleRerunTerm}
              rerunningTerm={rerunningTerm}
              isChecking={isChecking}
              checkingProgress={checkingProgress}
            />
          )}
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-gray-500 dark:text-gray-400">
              Select a company and product to start checking Amazon indexing
            </p>
          </CardContent>
        </Card>
      )}

      {/* Google Sheets Import Modal */}
      <GoogleSheetsImportModal
        isOpen={showSheetsModal}
        onClose={handleSheetsModalClose}
        onSuccess={handleSheetsImportSuccess}
      />


      {/* Show success notification for sync operations with 0 new terms (not showing form) */}
      {sheetsImportResult && sheetsImportResult.success && sheetsImportResult.newTerms === 0 && !showAddTerms && (
        <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 mb-6">
          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-700 dark:text-green-300 pr-8">
            <div className="font-medium mb-1">Google Sheets Sync Complete!</div>
            <div className="text-sm space-y-1">
              <div>• {sheetsImportResult.newTerms} new terms imported</div>
              <div>• {sheetsImportResult.duplicates} duplicates skipped</div>
              <div>• {sheetsImportResult.totalTerms} total terms in sheet</div>
              <div className="mt-2 text-xs text-green-600 dark:text-green-400">
                All terms were already imported. No new terms to check.
              </div>
            </div>
          </AlertDescription>
          <button
            onClick={handleDismissImportNotification}
            className="absolute top-2 right-2 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
            aria-label="Close notification"
          >
            ×
          </button>
        </Alert>
      )}

      {/* Image Modal */}
      <ImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        images={images}
        currentIndex={currentImageIndex}
        onPrevious={handlePrevious}
        onNext={handleNext}
        alt={productTitle}
      />
    </div>
  );
};

export default AmazonIndexCheckerPage;